/**
 * Word Search rules (pure functions). The grid and word list come from the
 * server (generated there with a stored seed); answer positions never reach
 * the client. A selection is a straight line between two cells — horizontal,
 * vertical or diagonal — and counts when its letters spell a remaining word
 * in a direction enabled for the level (either end may be selected first).
 * Must stay in step with App\Services\Games\GamePlayVerifier (server check).
 */

export const DIRECTIONS = {
  right: [0, 1],
  down: [1, 0],
  down_right: [1, 1],
  up_right: [-1, 1],
  left: [0, -1],
  up: [-1, 0],
  up_left: [-1, -1],
  down_left: [1, -1],
};

export function directionKey(dr, dc) {
  const keys = Object.keys(DIRECTIONS);
  for (let i = 0; i < keys.length; i++) {
    if (DIRECTIONS[keys[i]][0] === dr && DIRECTIONS[keys[i]][1] === dc) return keys[i];
  }
  return "";
}

/** Cells from start to end, or null when the two cells are not on one straight line. */
export function lineCells(start, end) {
  if (!start || !end) return null;
  const dr = end[0] - start[0];
  const dc = end[1] - start[1];
  if (dr !== 0 && dc !== 0 && Math.abs(dr) !== Math.abs(dc)) return null;
  const stepR = Math.sign(dr);
  const stepC = Math.sign(dc);
  const length = Math.max(Math.abs(dr), Math.abs(dc)) + 1;
  const cells = [];
  for (let i = 0; i < length; i++) cells.push([start[0] + stepR * i, start[1] + stepC * i]);
  return cells;
}

export function lettersOf(grid, cells) {
  return cells.map((cell) => (grid[cell[0]] || "").charAt(cell[1])).join("");
}

/**
 * The remaining word a selection spells (answer string), or null.
 * @param {string[]} grid
 * @param {number[]} start [row, col]
 * @param {number[]} end [row, col]
 * @param {{answer: string}[]} words
 * @param {string[]} directions enabled direction keys
 * @param {Object<string, boolean>} found answers already found
 */
export function matchSelection(grid, start, end, words, directions, found) {
  const cells = lineCells(start, end);
  if (!cells || cells.length < 2) return null;
  const letters = lettersOf(grid, cells);
  const forward = directionKey(Math.sign(end[0] - start[0]), Math.sign(end[1] - start[1]));
  const backward = directionKey(Math.sign(start[0] - end[0]), Math.sign(start[1] - end[1]));
  const reversed = letters.split("").reverse().join("");
  for (let i = 0; i < words.length; i++) {
    const answer = words[i].answer;
    if (found && found[answer]) continue;
    if (letters === answer && directions.indexOf(forward) >= 0) return answer;
    if (reversed === answer && directions.indexOf(backward) >= 0) return answer;
  }
  return null;
}

/** Cell under a point inside the grid's bounding rect (null outside). */
export function cellAtPoint(rect, rows, cols, clientX, clientY) {
  const x = clientX - rect.left;
  const y = clientY - rect.top;
  if (x < 0 || y < 0 || x >= rect.width || y >= rect.height) return null;
  return [
    Math.min(rows - 1, Math.floor((y / rect.height) * rows)),
    Math.min(cols - 1, Math.floor((x / rect.width) * cols)),
  ];
}

/**
 * Snap a drag end to the nearest straight line from the start (so a slightly
 * wobbly diagonal drag still selects a diagonal).
 */
export function snapEnd(start, end, rows, cols) {
  if (!start || !end) return end;
  const dr = end[0] - start[0];
  const dc = end[1] - start[1];
  if (dr === 0 || dc === 0 || Math.abs(dr) === Math.abs(dc)) return end;
  const adr = Math.abs(dr);
  const adc = Math.abs(dc);
  let target;
  if (adr > 2 * adc) target = [end[0], start[1]];
  else if (adc > 2 * adr) target = [start[0], end[1]];
  else {
    const n = Math.min(adr, adc);
    target = [start[0] + Math.sign(dr) * n, start[1] + Math.sign(dc) * n];
  }
  return [
    Math.max(0, Math.min(rows - 1, target[0])),
    Math.max(0, Math.min(cols - 1, target[1])),
  ];
}

/** Proof for the server: each found word with the selected start / end cells. */
export function completionProof(foundList) {
  return {
    found: foundList.map((f) => ({ word: f.answer, start: f.start.slice(), end: f.end.slice() })),
  };
}
